import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { getCurrentUser } from '../lib/supabase/client';
import { onAuthStateChange } from '../lib/supabase/auth';
import signalRService from '../lib/api/signalRService';

// Create the authentication context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const previousUserRef = useRef(null);

  useEffect(() => {
    // Check for user on initial load
    const loadUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser || null);
        previousUserRef.current = currentUser;
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();

    // Set up auth state change listener
    const subscription = onAuthStateChange(async (user, event) => {
      // If the user changes (logout, login as different user, etc.), reset SignalR
      const previousUser = previousUserRef.current;
      if ((!user && previousUser) || (user && previousUser && user.id !== previousUser.id)) {
        console.log('User changed, resetting SignalR service');
        try {
          await signalRService.resetService();
        } catch (error) {
          console.error('Error resetting SignalR during auth change:', error);
        }
      }
      
      setUser(user);
      previousUserRef.current = user;
      setLoading(false);
    });

    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Auth context value
  const value = {
    user,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for accessing auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 